import React, { useEffect } from 'react';
import { PageWithNavigation } from '@/src/components';
import { Button, Heading4, theme, Margin, P } from '@ovotech/element-v5';
import { useHistory, useLocation } from 'react-router-dom-v5';
import { ROUTE_ANYTIME_ELIGIBILITY } from '@/src/constants/routes';
import {
  SwitchFalloutReason,
  switchFalloutContent,
  EV,
  getPlanNameFromBolton,
  useChargeAnytimeProducts,
} from '@monovo/ev-anytime';
import { PageHeaderV2 } from '../../components/PageHeaderV2';
import { Media } from '@/src/components/Media';
import { breakpoints } from '@/src/utils/mediaQuery';
import styled from 'styled-components';
import { useAnalytics } from '@ovotech/ui-tools';
import { useChargeAnytimeBoltonState } from '@/src/pages/AnytimeEligibilityV2/hooks/useChargeAnytimeBoltonState';

interface SwitchFalloutProps {
  reason: SwitchFalloutReason;
}

const StyledHeading4 = styled(Heading4)`
  font-size: 20px !important;
  color: ${theme.color.surface.onSurface};
`;

const StyledCard = styled.div`
  padding: 24px;
  border-radius: 4px;
  background-color: white;
  margin: 32px 0;
  ${({ theme }) => theme.mediaQueries.mediumAndUp} {
    padding: 16px;
  },
`;

const Explanation = ({ reason }: SwitchFalloutProps) => {
  const history = useHistory();
  const goToSwitchPage = () => {
    history.push(ROUTE_ANYTIME_ELIGIBILITY.SWITCH);
  };

  return (
    <>
      <StyledCard>
        <StyledHeading4>{switchFalloutContent.title(reason)}</StyledHeading4>
        <div data-testid={`switch-fallout-explanation-${reason}`}>
          {switchFalloutContent.explanationLines(reason).map((line, i) => (
            <Margin key={line} top={i > 0 ? 4 : 0}>
              <P style={{ color: '#000' }}>{line}</P>
            </Margin>
          ))}
        </div>
        <Media query={breakpoints.mediumDown}>
          <Button
            variant="outline"
            iconLeft="chevron-left"
            onClick={goToSwitchPage}
            data-testid="back-button"
            style={{ fontWeight: 800, marginTop: 16 }}
          >
            Back
          </Button>
        </Media>
      </StyledCard>
      <Media query={breakpoints.mediumUp}>
        <div style={{ textAlign: 'right' }}>
          <Button
            variant="outline"
            iconLeft="chevron-left"
            onClick={goToSwitchPage}
            data-testid="back-button"
            style={{ fontWeight: 800 }}
          >
            Back
          </Button>
        </div>
      </Media>
    </>
  );
};

export const SwitchFallout = () => {
  const { dispatch: analyticsDispatch } = useAnalytics();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const reason =
    (searchParams.get('reason') as SwitchFalloutReason) || 'GENERAL_ERROR';

  const { boltonPlan } = useChargeAnytimeBoltonState();

  const { data: products } = useChargeAnytimeProducts('all');

  useEffect(() => {
    if (boltonPlan && products) {
      analyticsDispatch({
        type: 'view',
        name: EV.ELIGIBILITY.CHARGE_ANYTIME_SWITCH_SUCCESS_SCREEN_VIEWED,
        properties: {
          currentPlanName: getPlanNameFromBolton(boltonPlan.current, products),
          pathname: '/anytime/switch-fallout',
          journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
          screen: EV.SCREENS.FAILED_SWITCH,
        },
      });
    }
  }, [boltonPlan]);

  return (
    <PageWithNavigation
      title={switchFalloutContent.pageTitle(reason).join(' ')}
    >
      <PageHeaderV2
        title={switchFalloutContent.pageTitle(reason).join(' ')}
        testId="introduction-header"
      />
      <Margin bottom={8} />
      <Explanation reason={reason} />
    </PageWithNavigation>
  );
};
